import { RequestMode } from '@internal/backstage-plugin-uptime-monitor-backend/src/enum';

export type UptimeMonitorRequest = {
  host: string;
  org: string;
  monitorId: string;
  mode?: RequestMode;
  query?: {
    limit?: number;
    is_change?: boolean;
    cursor?: string;
  };
};

export type HealthCheck = {
  isUp: boolean;
  startCheck: string;
  reason: number | null;
  responseTime: number;
};

export type UptimeMonitorResponse = {
  expectedBody: string;
  expectedStatus: number;
  timeout: number | null;
  monitorType: string;
  name: string;
  url: string;
  project: number;
  interval: number;
  environment: string | null;
  isUp: boolean;
  lastChange: string;
  heartbeatEndpoint: string | null;
  projectName: string;
  envName: string | null;
  checks: HealthCheck[];
};

export type MonitorChecksHistoryResponse = {
  checks: HealthCheck[];
  pagination: PaginationInfo;
};

export type PaginationInfo = {
  next: string | undefined;
  previous: string | undefined;
};

export enum TimeRange {
  Today = 'today',
  ThisWeek = 'thisWeek',
  ThisMonth = 'thisMonth',
  ThisYear = 'thisYear',
}

export type UptimeMetricRequest = {
  org: string;
  monitorId: string;
  host: string;
  query?: {
    start_date?: string;
    end_date?: string;
    is_change?: boolean;
  };
};

export type UptimeMetricResponse = {
  uptimePercentage: number;
  periodStart: string;
  periodEnd: string;
};

export type IssuesRequest = {
  host: string;
  org: string;
  query?: {
    query?: string;
    projectId?: string;
    start?: string;
    end?: string;
    sort?: string;
    environment?: string;
    limit?: number;
    cursor?: string;
  };
};

export type Issue = {
  id: string;
  count: string;
  type: string;
  level: string;
  status: 'resolved' | 'unresolved' | 'ignored';
  project: {
    id: string;
    platform: string;
    slug: string;
    name: string;
  };
  shortId: string;
  numComments: number;
  stats: {
    '24h': any[];
  };
  shareId: string | null;
  logger: string | null;
  permalink: string;
  statusDetails: Record<string, any>;
  subscriptionDetails: any;
  userCount: number;
  matchingEventId: string | null;
  title: string;
  metadata: {
    type: string;
    value: string;
    filename: string;
    function: string;
  };
  culprit: string;
  firstSeen: string;
  lastSeen: string;
  userReportCount?: number;
};

export type IssuesListResponse = {
  issues: Issue[];
  pagination: PaginationInfo;
};

export type IssueDetailRequest = {
  host: string;
  id: string;
};

export interface IssueBasic {
  id: string;
  status: string;
  level: string;
  firstSeen: string;
  lastSeen: string;
  count: string;
  metadata: {
    type: string;
    value: string;
  };
  project: {
    name: string;
    platform: string;
  };
}

interface EventEntry {
  type: string;
  data?: any;
}

interface EventDetails {
  eventID: string;
  platform: string;
  dateCreated: string;
  entries: EventEntry[];
  contexts?: EventContexts;
  packages?: Record<string, string>;
}

export interface IssueDetailData {
  issue: IssueBasic;
  eventDetails: EventDetails;
}

export interface IssueEventLatestRequest {
  host: string;
  id: string;
}

export interface StackFrame {
  filename: string;
  function: string;
  lineNo: number;
  context_line: string;
  inApp: boolean;
  absPath: string;
  context?: Array<[number, string]>;
  vars?: Record<string, any>;
  raw_function?: string;
}

export interface StackTrace {
  frames: StackFrame[];
  hasSystemFrames?: boolean;
}

export interface ExceptionValue {
  type: string;
  value: string;
  mechanism: {
    type: string;
    handled: boolean;
  };
  stacktrace: {
    frames: StackFrame[];
  };
}

export interface ExceptionEntry {
  type: 'exception';
  data: {
    values: ExceptionValue[];
  };
}

export interface Breadcrumb {
  type: string;
  category: string;
  message: string;
  data?: Record<string, any>;
  level: string;
  timestamp: string;
  event_id?: string | null;
}

export interface BreadcrumbsEntry {
  type: 'breadcrumbs';
  data: {
    values: Breadcrumb[];
  };
}

export interface MessageEntry {
  type: 'message';
  data: {
    formatted: string;
  };
}

export interface RequestEntry {
  type: 'request';
  data: {
    method: string;
    url: string;
    headers: Array<[string, string]>;
    cookies?: any;
    data?: any;
    env?: any;
    fragment?: any;
    protocol?: any;
    query?: any;
    apiTarget?: any;
    bodySize?: any;
    inferredContentType?: any;
  };
}

export interface EventContexts {
  os?: {
    name: string;
    build: string;
    version: string;
    kernel_version: string;
  };
  runtime?: {
    name: string;
    sapi: string;
    version: string;
  };
  trace?: {
    span_id: string;
    trace_id: string;
  };
}

export interface BreadcrumbEntry {
  type: 'breadcrumbs';
  data: {
    values: Array<{
      category: string;
      message: string;
      timestamp: string;
      data?: any;
    }>;
  };
}

export interface IssueEventDetails {
  platform: string;
  id: string;
  eventID: string;
  projectID: number;
  groupID: string;
  dateCreated: string;
  dateReceived: string;
  dist: string | null;
  culprit: string;
  packages: Record<string, string>;
  type: string;
  message: string;
  metadata: {
    type: string;
    value: string;
    filename: string;
    function: string;
  };
  tags: Array<{
    key: string;
    value: string;
  }>;
  entries: Array<ExceptionEntry | BreadcrumbsEntry | MessageEntry | RequestEntry>;
  contexts: EventContexts;
  context: any;
  user: Record<string, any>;
  title: string;
  userReport: any;
  nextEventID: string | null;
  previousEventID: string | null;
}

export type IssueUpdateRequest = {
  host: string;
  org: string;
  status: 'resolved' | 'unresolved' | 'ignored';
  id?: number;
  ids?: number[];
};

export type IssueUpdateResponse = {
  message: string;
  result: any;
};

export enum IssueStatus {
  RESOLVED = 'resolved',
  UNRESOLVED = 'unresolved', 
  IGNORED = 'ignored',
}

export enum SortOption {
  LAST_SEEN = '-last_seen',
  FIRST_SEEN = 'last_seen',
  NEWEST_CREATION_DATE = '-created',
  OLDEST_CREATION_DATE = 'created',
  MOST_FREQUENT = '-count',
  LEAST_FREQUENT = 'count',
  HIGHEST_PRIORITY = '-priority',
  LOWEST_PRIORITY = 'priority',
}

export interface Comment {
  id: number;
  type: 'note';
  dateCreated: string;
  data: {
    text: string;
  };
  user: {
    id: string;
    email: string;
  };
}

export interface CommentRequest {
  text: string;
}

export interface IssueCommentsRequest {
  host: string;
  id: string;
}

export interface AddCommentRequest {
  host: string;
  id: string;
  text: string;
}

export interface UpdateCommentRequest {
  host: string;
  id: string;
  commentId: number;
  text: string;
}

export interface DeleteCommentRequest {
  host: string;
  id: string;
  commentId: number;
}

export interface StatusUpdateWithCommentRequest {
  host: string;
  org: string;
  issueId: number;
  status: IssueStatus;
  comment: string;
}

export interface GlitchtipUptimeResult {
  success: boolean;
  error?: string;
  data: {
    healthCheckUrl: string;
    host: string;
    org: string;
    monitorId: string;
  } | null;
}

export type GlitchtipErrorResult = {
  success: true;
  data: {
    errorsCheckUrl: string;
    host: string;
    org: string;
    projectId: string;
  };
} | {
  success: false;
  error: string;
  data: null;
};

export type TransactionGroup = {
  id: number;
  transaction: string;
  op: string;
  method: string | null;
  avgDuration: number;
  transactionCount: number;
  project: number;
};

export type TransactionGroupsRequest = {
  host: string;
  org: string;
  query?: {
    project?: string;
    start?: string;
    end?: string;
    sort?: string;
    query?: string;
    cursor?: string;
    limit?: number;
  };
};

export type TransactionGroupsListResponse = {
  transactionGroups: TransactionGroup[];
  pagination: PaginationInfo;
};

export type TransactionGroupDetailRequest = {
  host: string;
  org: string;
  id: string;
};

export enum PerformanceSortOption {
  NEWEST = '-created',
  OLDEST = 'created',
  SLOWEST = '-avg_duration',
  FASTEST = 'avg_duration',
  MOST_FREQUENT = '-transaction_count',
  LEAST_FREQUENT = 'transaction_count',
}

export type GlitchtipPerformanceResult = 
  | {
      success: true;
      data: {
        performanceUrl: string;
        host: string;
        org: string;
        projectId: string;
      };
      error?: never;
    }
  | {
      success: false;
      error: string;
      data: null;
    };

export const PERFORMANCE_SORT_LABELS: Record<PerformanceSortOption, string> = {
  [PerformanceSortOption.NEWEST]: 'Newest',
  [PerformanceSortOption.OLDEST]: 'Oldest', 
  [PerformanceSortOption.SLOWEST]: 'Slowest',
  [PerformanceSortOption.FASTEST]: 'Fastest',
  [PerformanceSortOption.MOST_FREQUENT]: 'Most Frequent',
  [PerformanceSortOption.LEAST_FREQUENT]: 'Least Frequent',
};

export interface CreateTaskFromIssueRequest {
  title: string;
  description: string;
  priority: string;
  project?: string;
  subtype?: string;
  [key: string]: any;
}

export interface Sev2ProjectResult {
  success: boolean;
  error?: string;
  data?: {
    projectBoardUrl: string;
    projectId: string;
  } | null;
}

export interface AISuggestionRequest {
  prompt: string;
  data_id: string;
  model?: string;
}

export interface AISuggestionResponse {
  id: string;
  content: string;
  provider: string;
  model: string;
  tokens_used: number;
  input_tokens: number;
  output_tokens: number;
  cost: number;
  completion_time_ms: number;
  user_id: string | null;
  context: {
    module: string;
    agent_type: string;
    original_prompt: string;
  };
  created_at: string;
}

export interface AIPredictionResponse {
  id: string;
  data_id: string;
  user_id: string | null;
  title: string;
  provider: string;
  model: string;
  persona: string;
  message: string;
  response: string;
  tokens_used: number;
  cost: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface GetAIPredictionRequest {
  data_id: string;
}

export interface GenerateAISuggestionRequest {
  prompt: string;
  data_id: string;
  model?: string;
}